import * as React from 'react';
import {
  Box,
  Tab,
  Paper,
  Grid,
  Typography,
  Button,
  Select,
  MenuItem,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Card,
  CardContent,
  CardHeader
} from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { styled } from '@mui/material/styles';
import { useParams } from 'react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router';
import moment from 'moment';
import { getApi } from 'views/services/api';
import AddHotel from '../AddHotel';

// icons
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapIcon from '@mui/icons-material/Map';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';

const InfoCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  height: '100%'
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.95rem',
  marginRight: theme.spacing(3),
  '&.Mui-selected': {
    color: theme.palette.secondary.main
  }
}));

const DataItem = ({ icon, label, value, isLink, linkUrl }) => (
  <Stack direction="row" spacing={2} sx={{ mb: 2.5 }}>
    <Avatar sx={{ bgcolor: 'rgba(33, 150, 243, 0.08)', color: '#2196F3', width: 40, height: 40 }}>
      {icon}
    </Avatar>
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      {isLink ? (
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          <a href={linkUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1e88e5', textDecoration: 'none' }}>
            {value}
          </a>
        </Typography>
      ) : (
        <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {value || 'N/A'}
        </Typography>
      )}
    </Box>
  </Stack>
);

export default function HotelManagementDashboard() {
  const navigate = useNavigate();
  const [value, setValue] = useState('1');
  const [openAdd, setOpenAdd] = useState(false);
  const [customerData, setCustomerData] = useState([]);
  const [hotelData, setHotelData] = useState([]);
  const params = useParams();
  const hotelId = params.id;

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const fetchHotelData = async () => {
    try {
      const response = await getApi(`api/hotel/view/${hotelId}`);
      setHotelData(response?.data || {});
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAllCustomer = async () => {
    try {
      const response = await getApi(`api/customer/viewallcustomer/${hotelId}`);
      setCustomerData(response.data.customerData || []);
    } catch (error) {
      console.error('Found error =>', error);
    }
  };

  useEffect(() => {
    fetchHotelData();
    fetchAllCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAdd]);

  return (
    <Box sx={{ width: '100%' }}>
      <AddHotel open={openAdd} handleClose={handleCloseAdd} />
      
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
              Dashboard de Hotel
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Detalles y gestión de {hotelData?.name || 'Cargando...'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ 
              borderRadius: '8px', 
              color: 'text.secondary', 
              borderColor: 'divider',
              textTransform: 'none',
              '&:hover': { borderColor: 'text.primary', bgcolor: 'transparent' }
            }}
          >
            Volver
          </Button>
        </Stack>
      </Box>

      <TabContext value={value}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <TabList onChange={handleChange} indicatorColor="secondary" textColor="secondary">
            <StyledTab label="Información General" value="1" />
            <StyledTab label="Clientes Registrados" value="2" />
          </TabList>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Select 
              value="" 
              displayEmpty 
              size="small"
              sx={{ 
                minWidth: 120, 
                borderRadius: '8px',
                '& .MuiSelect-select': { py: 1 }
              }}
            >
              <MenuItem value="" disabled>Acciones</MenuItem>
              <MenuItem onClick={handleOpenAdd} sx={{ color: 'success.main' }}>
                <AddIcon sx={{ mr: 1, fontSize: 18 }} /> Nuevo Hotel
              </MenuItem>
              <MenuItem onClick={() => navigate(`/dashboard/hotel/edit/${hotelData._id}`)}>
                <MoreVertIcon sx={{ mr: 1, fontSize: 18 }} /> Editar Perfil
              </MenuItem>
            </Select>
          </Box>
        </Box>

        <TabPanel value="1" sx={{ p: 0 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <InfoCard>
                <CardHeader 
                  title="Detalles del Establecimiento" 
                  titleTypographyProps={{ variant: 'h4', fontWeight: 700 }}
                  sx={{ pb: 0 }}
                />
                <CardContent sx={{ pt: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <DataItem icon={<BusinessIcon fontSize="small" />} label="Nombre del Hotel" value={hotelData?.name} />
                      <DataItem icon={<EmailIcon fontSize="small" />} label="Correo de Contacto" value={hotelData?.email} />
                      <DataItem icon={<PhoneIcon fontSize="small" />} label="Teléfono" value={hotelData?.contact} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DataItem icon={<LocationOnIcon fontSize="small" />} label="Dirección" value={hotelData?.address} />
                      <DataItem 
                        icon={<MapIcon fontSize="small" />} 
                        label="Ubicación en Google Maps" 
                        value="Ver en el mapa" 
                        isLink 
                        linkUrl={hotelData?.mapurl ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(hotelData.mapurl)}` : '#'} 
                      />
                      <DataItem icon={<MonetizationOnIcon fontSize="small" />} label="Moneda" value={hotelData?.currency || 'USD'} />
                    </Grid>
                  </Grid>
                  
                  <Divider sx={{ my: 3, borderStyle: 'dashed' }} />
                  
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>Configuración Fiscal y Fechas</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <DataItem icon={<ReceiptIcon fontSize="small" />} label="Número de RIF / GST" value={hotelData?.gstNumber} />
                      <DataItem icon={<CreditCardIcon fontSize="small" />} label="IVA Habitaciones" value={`${hotelData?.roomgstpercentage || 0}%`} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DataItem icon={<EventIcon fontSize="small" />} label="Fecha de Registro" value={hotelData?.createdDate ? moment(hotelData.createdDate).format('DD MMMM, YYYY') : 'N/A'} />
                      <DataItem icon={<LanguageOutlinedIcon fontSize="small" />} label="Zona Horaria" value={hotelData?.timezone} />
                    </Grid>
                  </Grid>
                </CardContent>
              </InfoCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <InfoCard sx={{ bgcolor: 'rgba(33, 150, 243, 0.02)' }}>
                <CardContent sx={{ textAlign: 'center', py: 5 }}>
                  <Avatar 
                    sx={{ 
                      width: 100, 
                      height: 100, 
                      mx: 'auto', 
                      mb: 2, 
                      bgcolor: 'secondary.main',
                      fontSize: '2.5rem',
                      fontWeight: 700,
                      boxShadow: '0 8px 16px 0 rgba(103, 58, 183, 0.24)'
                    }}
                  >
                    {hotelData?.name?.charAt(0)}
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {hotelData?.name}
                  </Typography>
                  <Box 
                    sx={{ 
                      display: 'inline-flex', 
                      px: 2, 
                      py: 0.5, 
                      borderRadius: '20px', 
                      bgcolor: 'rgba(1, 181, 116, 0.12)', 
                      color: '#01B574',
                      fontWeight: 700,
                      fontSize: '0.75rem'
                    }}
                  >
                    HOTEL ACTIVO
                  </Box>
                  
                  <Box sx={{ mt: 4, textAlign: 'left' }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                      Resumen Rápido
                    </Typography>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Total Clientes:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{customerData?.length || 0}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Región:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{hotelData?.timezone?.split('/')[0] || 'N/A'}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                </CardContent>
              </InfoCard>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value="2" sx={{ p: 0 }}>
          <InfoCard>
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ bgcolor: '#f4f6f8' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Contacto</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Dirección</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Identificación</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customerData.length > 0 ? customerData.map((customer) => (
                    <TableRow key={customer._id} sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' } }}>
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }} title={customer.fullName}>
                            <PersonIcon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{customer.fullName}</Typography>
                            <Typography variant="caption" color="text.secondary">{customer.email}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{customer.phoneNumber}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {customer.address}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{customer.idCardType}</Typography>
                        <Typography variant="caption" color="text.secondary">{customer.idcardNumber}</Typography>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                        <Typography variant="body1" color="text.secondary">No hay clientes registrados en este hotel todavía.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </InfoCard>
        </TabPanel>
      </TabContext>
    </Box>
  );
}
